import React from "react";
import { Link } from "react-router-dom";
import { Mail, Twitter, Linkedin, Github, Youtube } from "lucide-react";

const FOOTER_LINKS = {
  Product: [
    { name: "Email Validation", href: "/product/email-validation" },
    { name: "Bulk Validation", href: "/product/bulk-validation" },
    { name: "API Access", href: "/product/api" },
    { name: "Chrome Extension", href: "/product/extension" },
  ],
  Solutions: [
    { name: "Marketing Teams", href: "/solutions/marketing" },
    { name: "SaaS", href: "/solutions/saas" },
    { name: "Agencies", href: "/solutions/agencies" },
    { name: "CRMs", href: "/solutions/crms" },
  ],
  Integrations: [
    { name: "Zapier", href: "/integrations/zapier" },
    { name: "HubSpot", href: "/integrations/hubspot" },
    { name: "Salesforce", href: "/integrations/salesforce" },
    { name: "Mailchimp", href: "/integrations/mailchimp" },
  ],
  Resources: [
    { name: "Blog", href: "/resources/blog" },
    { name: "Guides", href: "/resources/guides" },
    { name: "Case Studies", href: "/resources/case-studies" },
    { name: "Best Practices", href: "/resources/best-practices" },
  ],
  Company: [
    { name: "About", href: "/company/about" },
    { name: "Careers", href: "/company/careers" },
    { name: "Security", href: "/company/security" },
    { name: "Partners", href: "/company/partners" },
  ],
  Support: [
    { name: "Help Center", href: "/support/help" },
    { name: "FAQ", href: "/support/faq" },
    { name: "Contact", href: "/support/contact" },
  ],
};

export default function Footer() {
  return (
    <footer className="bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 mb-12">
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img 
                src="https://cdn-icons-png.flaticon.com/128/12795/12795772.png" 
                alt="ValidMail" 
                className="h-8"
              />
              <span className="text-xl font-bold text-slate-900 dark:text-slate-50">
                ValidMail
              </span>
            </Link>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 leading-relaxed">
              Modern, high-accuracy email validation platform. Built for teams
              who care about deliverability, speed, and privacy.
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://twitter.com/validmail"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-slate-200 dark:bg-slate-800 hover:bg-sky-100 dark:hover:bg-sky-900 flex items-center justify-center transition-colors group"
              >
                <Twitter className="h-4 w-4 text-slate-600 dark:text-slate-400 group-hover:text-sky-600 dark:group-hover:text-sky-400" />
              </a>
              <a
                href="https://linkedin.com/company/validmail"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-slate-200 dark:bg-slate-800 hover:bg-sky-100 dark:hover:bg-sky-900 flex items-center justify-center transition-colors group"
              >
                <Linkedin className="h-4 w-4 text-slate-600 dark:text-slate-400 group-hover:text-sky-600 dark:group-hover:text-sky-400" />
              </a>
              <a
                href="https://github.com/validmail"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-slate-200 dark:bg-slate-800 hover:bg-sky-100 dark:hover:bg-sky-900 flex items-center justify-center transition-colors group"
              >
                <Github className="h-4 w-4 text-slate-600 dark:text-slate-400 group-hover:text-sky-600 dark:group-hover:text-sky-400" />
              </a>
              <a
                href="https://youtube.com/@validmail"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-slate-200 dark:bg-slate-800 hover:bg-sky-100 dark:hover:bg-sky-900 flex items-center justify-center transition-colors group"
              >
                <Youtube className="h-4 w-4 text-slate-600 dark:text-slate-400 group-hover:text-sky-600 dark:group-hover:text-sky-400" />
              </a>
            </div>
          </div>

          {Object.entries(FOOTER_LINKS).map(([category, links]) => (
            <div key={category}>
              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-50 mb-4">
                {category}
              </h3>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-sm text-slate-600 dark:text-slate-400 hover:text-sky-600 dark:hover:text-sky-400 transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pt-8 border-t border-slate-200 dark:border-slate-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 text-sm text-slate-600 dark:text-slate-400">
              <span>&copy; 2025 ValidMail. All rights reserved.</span>
              <Link
                to="/legal/privacy"
                className="hover:text-sky-600 dark:hover:text-sky-400 transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/legal/terms"
                className="hover:text-sky-600 dark:hover:text-sky-400 transition-colors"
              >
                Terms of Service
              </Link>
              <Link
                to="/legal/cookies"
                className="hover:text-sky-600 dark:hover:text-sky-400 transition-colors"
              >
                Cookie Policy
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <a
                href="mailto:hello@validmail.io"
                className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-sky-600 dark:hover:text-sky-400 transition-colors"
              >
                <Mail className="h-4 w-4" />
                <span>hello@validmail.io</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-100 dark:bg-slate-950 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500 dark:text-slate-400">
            <p>
              Made with ❤️ for modern email marketers, product teams, and developers
            </p>
            <div className="flex items-center gap-4">
              <span>🇮🇳 India DPDP Ready</span>
              <span>🇪🇺 GDPR Compliant</span>
              <span>🔒 SOC 2 Type II</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
